import {
  Box,
  Flex,
  type FlexProps,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Tooltip,
  type createIcon,
} from "@chakra-ui/react"
import { Fragment } from "react"
import { NavLink, useMatch } from "react-router-dom"

import { inverseColors } from "@/theme/values"

import { AngleDownIcon, AngleRightIcon, ExternalLinkIcon } from "../Icons"

type Icon = ReturnType<typeof createIcon>

// Sidebar item (sidebar.css .bns-sidebar__item): 44px, 20px icon + 14px label, muted on the
// black rail; active = white semibold label + 2px red rule on the left edge.
const itemStyle: FlexProps = {
  position: "relative",
  align: "center",
  gap: "3",
  w: "100%",
  minH: "44px",
  pl: "26px",
  pr: "6",
  fontSize: "sm",
  fontWeight: "normal",
  lineHeight: 1.2,
  // The collapse toggle is a <button>, which centres its text by default.
  textAlign: "left",
  whiteSpace: "nowrap",
  color: inverseColors.textMuted,
  bg: "transparent",
  cursor: "pointer",
  transition: "color 140ms, background-color 140ms",
  _hover: {
    color: inverseColors.text,
    bg: inverseColors.surfaceHover,
  },
  _focusVisible: {
    outline: "2px solid",
    outlineColor: "focus",
    outlineOffset: "-2px",
  },
}

const activeStyle: FlexProps = {
  color: inverseColors.text,
  fontWeight: "semibold",
  _before: {
    content: '""',
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    w: "2px",
    bg: "accent",
  },
}

const ItemContent = ({
  icon: Icon,
  label,
  collapsed,
}: { icon: Icon; label: string; collapsed: boolean }) => (
  <>
    <Icon boxSize="20px" flex="none" aria-hidden />
    <Box
      as="span"
      flex="1 1 auto"
      minW={0}
      overflow="hidden"
      textOverflow="ellipsis"
      // The label stays in the accessible name when collapsed; the tooltip shows it visually.
      {...(collapsed
        ? {
            position: "absolute",
            w: "1px",
            h: "1px",
            clipPath: "inset(50%)",
            whiteSpace: "nowrap",
          }
        : {})}
    >
      {label}
    </Box>
  </>
)

interface Props {
  icon: Icon
  label: string
  path: string
  exact?: boolean
  collapsed: boolean
  onClick?: () => void
}

export default function MenuItem({ icon, label, path, exact, collapsed, onClick }: Props) {
  return (
    <Box as="li">
      <Tooltip label={label} placement="right" isDisabled={!collapsed} hasArrow>
        <Box display="block">
          <NavLink to={path} end={exact} style={{ display: "block" }} onClick={onClick}>
            {({ isActive }) => (
              <Flex
                {...itemStyle}
                {...(isActive ? activeStyle : {})}
                aria-current={isActive ? "page" : undefined}
              >
                <ItemContent icon={icon} label={label} collapsed={collapsed} />
              </Flex>
            )}
          </NavLink>
        </Box>
      </Tooltip>
    </Box>
  )
}

interface ToggleProps {
  icon: Icon
  label: string
  collapsed: boolean
  onClick: () => void
}

export const MenuToggle = ({ icon, label, collapsed, onClick }: ToggleProps) => (
  <Box as="li">
    <Tooltip label={label} placement="right" isDisabled={!collapsed} hasArrow>
      <Flex as="button" type="button" onClick={onClick} {...itemStyle} aria-expanded={!collapsed}>
        <ItemContent icon={icon} label={label} collapsed={collapsed} />
      </Flex>
    </Tooltip>
  </Box>
)

export interface MenuSubEntry {
  label: string
  path: string
  exact?: boolean
  isExternal?: boolean
}

export interface MenuSubGroup {
  // Heading of the group (Admin); the entries of a section without groups form a single one.
  label?: string
  entries: MenuSubEntry[]
}

// Sub-entry: 36px, indented under the section label (26px + 20px icon + 12px gap) on the rail,
// at the panel padding in the flyout; active = the item's white semibold label and red rule.
const subItemStyle = (inFlyout: boolean): FlexProps => ({
  ...itemStyle,
  gap: "2",
  minH: "36px",
  pl: inFlyout ? "4" : "58px",
  pr: "4",
  whiteSpace: "normal",
})

const groupLabelStyle = (inFlyout: boolean): FlexProps => ({
  pt: "3",
  pb: "1",
  pl: inFlyout ? "4" : "58px",
  pr: "4",
  fontSize: "xs",
  fontWeight: "semibold",
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: inverseColors.textMuted,
})

const SubEntries = ({
  label,
  groups,
  inFlyout,
  onClick,
}: { label: string; groups: MenuSubGroup[]; inFlyout: boolean; onClick?: () => void }) => (
  <Box as="ul" aria-label={label} listStyleType="none" m={0} p={0} pb={inFlyout ? "2" : "1"}>
    {groups.map((group, index) => (
      <Fragment key={group.label ?? index}>
        {group.label && (
          <Flex as="li" {...groupLabelStyle(inFlyout)}>
            {group.label}
          </Flex>
        )}
        {group.entries.map(entry => (
          <Box as="li" key={entry.path}>
            <NavLink
              to={entry.path}
              end={entry.exact}
              target={entry.isExternal ? "_blank" : undefined}
              style={{ display: "block" }}
              onClick={onClick}
            >
              {({ isActive }) => (
                <Flex {...subItemStyle(inFlyout)} {...(isActive ? activeStyle : {})}>
                  <Box as="span" flex="1 1 auto" minW={0}>
                    {entry.label}
                  </Box>
                  {entry.isExternal && <ExternalLinkIcon boxSize={4} flex="none" aria-hidden />}
                </Flex>
              )}
            </NavLink>
          </Box>
        ))}
      </Fragment>
    ))}
  </Box>
)

interface SectionProps extends Omit<Props, "exact"> {
  groups: MenuSubGroup[]
}

// Section with sub-entries (Network, Sharing, Resources, Admin, Settings). Expanded rail: an
// accordion driven by the route, the current section open under its label (which leads to the
// section's first page) and the others closed; the red rule marks the current sub-entry.
// Collapsed rail: the icon carries the rule and, on hover or focus, opens a flyout panel listing
// the sub-entries (Chakra gives a hover popover the tooltip role: the list carries the name).
// `onClick` closes the mobile panel on a sub-entry only, so opening a section keeps it visible.
export const MenuSection = ({ icon, label, path, groups, collapsed, onClick }: SectionProps) => {
  const isCurrent = useMatch({ path: `${path}/*` }) !== null

  if (collapsed) {
    return (
      <Box as="li">
        <Popover trigger="hover" placement="right-start" gutter={0} openDelay={0} isLazy>
          <PopoverTrigger>
            <Box display="block">
              <NavLink to={path} style={{ display: "block" }}>
                <Flex {...itemStyle} {...(isCurrent ? activeStyle : {})}>
                  <ItemContent icon={icon} label={label} collapsed />
                </Flex>
              </NavLink>
            </Box>
          </PopoverTrigger>
          <Portal>
            <PopoverContent
              w="240px"
              bg="surfaceInverse"
              color={inverseColors.text}
              borderWidth={0}
              borderRadius={0}
              boxShadow="overlay"
            >
              <Box px="4" pt="4" pb="2" fontSize="sm" fontWeight="semibold">
                {label}
              </Box>
              <SubEntries label={label} groups={groups} inFlyout onClick={onClick} />
            </PopoverContent>
          </Portal>
        </Popover>
      </Box>
    )
  }

  return (
    <Box as="li">
      <NavLink to={path} style={{ display: "block" }}>
        <Flex
          {...itemStyle}
          {...(isCurrent ? { color: inverseColors.text, fontWeight: "semibold" } : {})}
        >
          <ItemContent icon={icon} label={label} collapsed={false} />
          {isCurrent ? (
            <AngleDownIcon boxSize="16px" flex="none" aria-hidden />
          ) : (
            <AngleRightIcon boxSize="16px" flex="none" aria-hidden />
          )}
        </Flex>
      </NavLink>
      {isCurrent && <SubEntries label={label} groups={groups} inFlyout={false} onClick={onClick} />}
    </Box>
  )
}
